import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { workingGroupsService } from '../../services/workingGroupsService';
import apiHelper from '../../utils/apiHelper';

// Add stripHtml function
const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

const CreateWorkingGroupModal = ({ show, onHide, onCreated }) => {
    const [workingGroup, setWorkingGroup] = useState({
        Title: '',
        WorkingGroupName: '',
        WorkingGroupType: 'Working Group',
        SteeringGroupName: 'N/a',
        SteeringGroupType: 'N/a',
        StartDate0: '2023',
        EndDate0: '2025',
        PublishFlag: true
    });

    const [steeringGroups, setSteeringGroups] = useState([]);

    // Fetch existing Steering Groups on mount
    useEffect(() => {
        const fetchSteeringGroups = async () => {
            try {
                const groups = await workingGroupsService.getWorkingGroups();
                // Filter only Steering Groups
                const steeringGroupsList = groups
                    .filter(group => group.WorkingGroupType === 'Steering Group')
                    .map(group => group.WorkingGroupName);
                setSteeringGroups(steeringGroupsList);
            } catch (error) {
                console.error('Error fetching steering groups:', error);
            }
        };
        fetchSteeringGroups();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const workingGroupData = {
                Title: workingGroup.Title,
                WorkingGroupName: workingGroup.Title,
                WorkingGroupType: workingGroup.WorkingGroupType,
                SteeringGroupName: workingGroup.SteeringGroupName,
                SteeringGroupType: workingGroup.SteeringGroupType,
                StartDate0: workingGroup.StartDate0,
                EndDate0: workingGroup.EndDate0,
                PublishFlag: true
            };

            // Create the working group
            const response = await workingGroupsService.createWorkingGroup(workingGroupData);
            
            // Create structured content for indexing
            const workingGroupText = `[Type: Working Group]
[Title: ${workingGroupData.WorkingGroupName}]
[WorkingGroupType: ${workingGroupData.WorkingGroupType}]
[SteeringGroup: ${workingGroupData.SteeringGroupName}]
[StartDate: ${workingGroupData.StartDate0}]
[EndDate: ${workingGroupData.EndDate0}]

[Summary]
This is a ${workingGroupData.WorkingGroupType} working group focused on ${workingGroupData.WorkingGroupName}. 
${workingGroupData.SteeringGroupName !== 'N/a' ? `It is part of the ${workingGroupData.SteeringGroupName} steering group.` : ''}
The group is active from ${workingGroupData.StartDate0} to ${workingGroupData.EndDate0}.`;

            // Index the new working group
            await apiHelper.indexQdrantDocument(workingGroupText, {
                title: workingGroupData.WorkingGroupName,
                type: 'Working Group',
                workingGroupType: workingGroupData.WorkingGroupType,
                steeringGroup: workingGroupData.SteeringGroupName,
                startDate: workingGroupData.StartDate0,
                endDate: workingGroupData.EndDate0,
                updateCount: 0,
                hasTOR: false,
                torObjectiveCount: 0,
                listItemId: response.id,
                text: workingGroupText,
                content: workingGroupText
            }, response.id);

            onCreated();
            onHide();
            toast.success('Working group created and indexed successfully! 🎯');
        } catch (error) {
            console.error('Error creating working group:', error);
            toast.error('Failed to create working group. Please try again.');
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Create New Working Group</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Working Group Type *</Form.Label>
                        <Form.Select
                            value={workingGroup.WorkingGroupType}
                            onChange={(e) => {
                                const newType = e.target.value;
                                setWorkingGroup({
                                    ...workingGroup,
                                    WorkingGroupType: newType,
                                    // Reset steering group fields based on type
                                    SteeringGroupName: newType === 'Steering Group' ? workingGroup.Title : 'N/a',
                                    SteeringGroupType: newType === 'Steering Group' ? 'Steering Group' : 'N/a'
                                });
                            }}
                        >
                            <option value="Working Group">Working Group</option>
                            <option value="Steering Group">Steering Group</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Name *</Form.Label>
                        <Form.Control
                            type="text"
                            value={workingGroup.Title}
                            onChange={(e) => setWorkingGroup({
                                ...workingGroup,
                                Title: e.target.value,
                                WorkingGroupName: e.target.value,
                                SteeringGroupName: workingGroup.WorkingGroupType === 'Steering Group' ? e.target.value : workingGroup.SteeringGroupName
                            })}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Steering Group</Form.Label>
                        <Form.Select
                            value={workingGroup.SteeringGroupName}
                            onChange={(e) => setWorkingGroup({
                                ...workingGroup,
                                SteeringGroupName: e.target.value,
                                SteeringGroupType: e.target.value === 'N/a' ? 'N/a' : 'Steering Group'
                            })}
                        >
                            <option value="N/a">No Steering Group</option>
                            {steeringGroups.map(group => (
                                <option key={group} value={group}>
                                    {group}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                            type="text"
                            value={workingGroup.StartDate0}
                            onChange={(e) => setWorkingGroup({
                                ...workingGroup,
                                StartDate0: e.target.value
                            })}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                            type="text"
                            value={workingGroup.EndDate0}
                            onChange={(e) => setWorkingGroup({
                                ...workingGroup,
                                EndDate0: e.target.value
                            })}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    Create Working Group
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateWorkingGroupModal;
